<template>
  <TableCustomMini
    ref="linksTable"
    class="table-custom-mini"
    :fields="fields"
    :hasArchiveButton="store.getters.hasLinkEditorAccess"
    :hasCreateButton="store.getters.hasLinkCreatorAccess"
    :hasDeleteButton="store.getters.hasLinkAdminAccess"
    :hasEditButton="store.getters.hasLinkEditorAccess"
    :hasMenu="store.getters.hasLinkEditorAccess"
    :items="linkItems"
    :itemType="t('general__data__links')"
    :selectedId="selectedId"
    @archiveItem="emits('archiveLink')"
    @deleteItem="emits('deleteLink')"
    @editItem="toggleEditLink"
    @loadMore="emits('loadMoreLinks')"
    @menuClosed="piniaStore.setSelectedLink(null)"
    @menuOpened="setFullLinkById($event)"
  >
    <!-- :hasEditButton="store.getters.hasLinkEditorAccess"  --add when edit enabled-->
    <template #preview="{ item }">
      <ButtonText
        class="table-custom-mini__button"
        :class="{ selected: item.id === selectedId }"
        :iconLeft="iconExternalLinkSmall"
        size="extra-small"
        :whiteContent="false"
        @onClicked="openLink(item)"
      ></ButtonText>
    </template>
  </TableCustomMini>
</template>
<script lang="ts" setup>
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useGeneralStore } from '@/pinia'
import { useStore } from 'vuex'
import ButtonText from '@/design-system/atoms/buttons/ButtonText.vue'
import TableCustomMini, { TableItem } from '@/design-system/molecules/TableCustomMini.vue'
import { Attachment } from '@/services/attachments.service'
import iconExternalLinkSmall from '@/design-system/assets/icons/Icon-External-Link-Small.svg'

type Props = {
  fields: any[]
  items?: Attachment[]
}
const props = defineProps<Props>()
type Emits = {
  (e: 'archiveLink'): void
  (e: 'deleteLink'): void
  (e: 'loadMoreLinks'): void
}
const emits = defineEmits<Emits>()
const { t } = useI18n()

const piniaStore = useGeneralStore()
const store = useStore()

const linksTable = ref()

const currentOverlayModalPinia = computed(() => {
  return piniaStore.currentOverlayModalPinia
})
const linkItems = computed<TableItem[]>(() => {
  if (!props.items) return []
  return props.items.map((item) => {
    return {
      id: item.attachmentId,
      name: item.name,
      entityId: item.entityId,
      active: true // Currently, we expect all links in the list to be active (not archived or deleted)
    }
  })
})
const selectedId = computed(() => {
  return piniaStore.selectedLink?.attachmentId ?? ''
})
const showOverlayModalPinia = computed(() => {
  return piniaStore.showOverlayModalPinia
})

function openLink(item: TableItem) {
  if (!props.items) return
  const link = props.items.find((link) => link.attachmentId === item.id)
  if (link) window.open(link.url)
}
function setFullLinkById(id: string) {
  if (!props.items) return
  let link = props.items.find((link) => link.attachmentId === id)
  if (link) piniaStore.setSelectedLink(link)
}
watch(showOverlayModalPinia, () => {
  if (!showOverlayModalPinia.value) {
    piniaStore.setSelectedLink(null)
  }
})
watch(
  () => piniaStore.selectedLink,
  () => {
    if (!piniaStore.selectedLink) {
      linksTable.value.closeMenu()
    }
  }
)
function toggleEditLink(item: TableItem | null) {
  linksTable.value.closeMenu()
  if (!item) return piniaStore.setSelectedLink(null)
  setFullLinkById(item.id)
  if (showOverlayModalPinia.value && currentOverlayModalPinia.value === 'editTicket') {
    // For tickets, modal on modal was a joint decision on prod/tech review 2024-08-19
    piniaStore.setCurrentOverlayModalOnModalPinia('editTicketLink')
    piniaStore.setShowOverlayModalOnModalPinia(true)
    return
  }
  piniaStore.setCurrentOverlayModalPinia('editLink')
  piniaStore.setShowOverlayModalPinia(true)
}
</script>
<style lang="scss" scoped>
@use '@/design-system/design-tokens/font-mixins';
@use '@/design-system/design-tokens/variables.scss';

.table-custom-mini {
  flex: 1 1 auto;
  min-height: calc((32px * 6) + 16px); // 6 rows + 16px padding
  padding: 16px variables.$padding-main-body;
  padding-top: 0px;
  z-index: 1;
  &__button {
    padding: 0;
    &.selected {
      background-color: variables.$color-primary;
      :deep(img) {
        filter: brightness(100);
      }
    }
    &:hover:not(.selected) {
      background-color: rgba(205, 222, 254, 0.2);
    }
  }
}
</style>
